/**
 * Save VoxPont to the phone home screen (Android Chrome install prompt, iOS A2HS).
 *
 * iOS cannot be triggered from script — Safari Share → Add to Home Screen is the
 * only path. Android Chrome can show a native prompt after this module registers
 * the installability service worker and a VoxPont web manifest.
 */

import { isLikelyIosDevice } from 'utils/isLikelyIosDevice';
import {
  isLikelyAndroidDevice,
  isVoxPontInterpreterPath,
} from './voxPontImmersiveChrome';

export const VOXPONT_INSTALL_MANIFEST_URL = '/voxpont.webmanifest';
export const VOXPONT_INSTALL_SW_URL = '/voxpont-sw.js';
export const FUSIONMSI_MANIFEST_URL = '/fusionmsi.webmanifest';

/** Offline PWAs that already register their own worker + manifest. */
export const VOXPONT_SPECIAL_PWA_ROUTES = Object.freeze([
  '/voxpont302',
  '/voxpont501',
]);

const APPLE_WEB_APP_METAS = [
  { name: 'apple-mobile-web-app-capable', content: 'yes' },
  { name: 'mobile-web-app-capable', content: 'yes' },
  { name: 'apple-mobile-web-app-title', content: 'VoxPont' },
  { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
];

let deferredInstallPrompt = null;
const promptListeners = new Set();

function notifyPromptListeners() {
  promptListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn('[VoxPont] save-as-app listener failed', err);
    }
  });
}

export function subscribeVoxPontInstallPrompt(listener) {
  promptListeners.add(listener);
  return () => {
    promptListeners.delete(listener);
  };
}

export function hasDeferredVoxPontInstallPrompt() {
  return Boolean(deferredInstallPrompt);
}

export function captureVoxPontBeforeInstallPrompt(event) {
  event.preventDefault();
  deferredInstallPrompt = event;
  notifyPromptListeners();
}

export function clearDeferredVoxPontInstallPrompt() {
  deferredInstallPrompt = null;
  notifyPromptListeners();
}

export async function promptVoxPontInstall() {
  if (!deferredInstallPrompt) return { outcome: 'unavailable' };
  const event = deferredInstallPrompt;
  deferredInstallPrompt = null;
  notifyPromptListeners();
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice || { outcome: 'dismissed' };
  } catch (err) {
    console.warn('[VoxPont] install prompt failed', err);
    return { outcome: 'error' };
  }
}

export function isVoxPontStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  if (window.navigator?.standalone === true) return true;
  return window.matchMedia?.('(display-mode: standalone)')?.matches === true;
}

/** Chrome/Firefox/Edge on iOS cannot Add to Home Screen — Safari can. */
export function isIosSafariForAddToHome() {
  if (!isLikelyIosDevice()) return false;
  const ua = navigator.userAgent || '';
  if (/CriOS|FxiOS|EdgiOS|OPiOS|Android/i.test(ua)) return false;
  return true;
}

export function usesOwnVoxPontPwa(pathname) {
  if (!pathname) return false;
  const lower = String(pathname).split('?')[0].toLowerCase();
  return VOXPONT_SPECIAL_PWA_ROUTES.some(
    (route) => lower === route || lower.startsWith(`${route}/`),
  );
}

export function shouldPrepareVoxPontInstall(pathname) {
  return isVoxPontInterpreterPath(pathname) && !usesOwnVoxPontPwa(pathname);
}

/**
 * @returns {'installed'|'ios-safari'|'ios-use-safari'|'android-prompt'|'android-menu'|'desktop'}
 */
export function getVoxPontSaveAsAppMode() {
  if (typeof window === 'undefined') return 'desktop';
  if (isVoxPontStandaloneDisplay()) return 'installed';
  if (isLikelyIosDevice()) {
    return isIosSafariForAddToHome() ? 'ios-safari' : 'ios-use-safari';
  }
  if (hasDeferredVoxPontInstallPrompt()) return 'android-prompt';
  if (isLikelyAndroidDevice()) return 'android-menu';
  return 'desktop';
}

export async function registerVoxPontInstallServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register(VOXPONT_INSTALL_SW_URL, {
      scope: '/',
    });
  } catch (err) {
    console.warn('[VoxPont] install service worker failed', err);
    return null;
  }
}

function ensureLink(rel) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaByName(name) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/**
 * Point the document at the VoxPont standalone manifest and add iOS home-screen
 * meta tags. start_url in the static manifest is /voxpont12 (flagship); iOS Add
 * to Home Screen still launches the page the user saved from.
 * @returns {() => void} cleanup that restores FusionMSI manifest + prior meta
 */
export function applyVoxPontInstallDocument(_startPath) {
  if (typeof document === 'undefined') return () => {};
  const restores = [];

  const manifestLink = ensureLink('manifest');
  const prevManifestHref = manifestLink.getAttribute('href');
  manifestLink.setAttribute('href', VOXPONT_INSTALL_MANIFEST_URL);
  restores.push(() => {
    if (prevManifestHref != null) {
      manifestLink.setAttribute('href', prevManifestHref);
    } else {
      manifestLink.setAttribute('href', FUSIONMSI_MANIFEST_URL);
    }
  });

  APPLE_WEB_APP_METAS.forEach(({ name, content }) => {
    const { el, created } = ensureMetaByName(name);
    const prev = created ? null : el.getAttribute('content');
    el.setAttribute('content', content);
    restores.push(() => {
      if (created) {
        el.remove();
      } else if (prev != null) {
        el.setAttribute('content', prev);
      } else {
        el.removeAttribute('content');
      }
    });
  });

  return () => {
    for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
  };
}
